import type { CurrentUser } from "@estelle/domain";

import { apiFetch, UnauthorizedError } from "@/adapters/api/base";
import {
  clearStoredSession,
  hasStoredSession,
  writeStoredSession,
} from "@/auth/session";
import { LIVE } from "@/lib/live";

/**
 * Signing in, signing out, and who is signed in (FR-ACL-1, M10-2).
 *
 * Two modes, decided the same way `DataSourceProvider` decides where data
 * comes from: with an API configured this is real — a password checked by
 * Better-Auth and a session cookie — and without one it is the demo session
 * that let every screen be built before a backend existed. One switch
 * (`lib/live.ts`), so the two cannot disagree about whether someone is
 * signed in — and so a build with an API drops the demo half entirely: the
 * branches below that run on `!LIVE` are not in it (#163).
 */

/** The sign-in form's reason to show a message rather than a stack trace. */
export class SignInError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "SignInError";
  }
}

/**
 * The answer to "who am I" that the route guard goes by.
 *
 * The guard runs on every navigation and cannot pay for a request each time, so
 * it reads this cached answer. Cleared on sign-in and sign-out, which are the
 * moments the answer changes for reasons this tab knows about — and replaced by
 * `refreshCurrentUser()`, which is how it learns of the ones it does not.
 *
 * **Not what the screens read their permissions from.** They go through
 * `refreshCurrentUser()`, because the permissions in this answer change while a
 * page is open: someone edits a role, a deploy adds a key to Administrator. A
 * page that only ever read this one saw the permissions it loaded with until
 * it was reloaded — an Administrator holding `comments.create` was shown a
 * comment thread with no box to write in (#498).
 */
let pending: Promise<CurrentUser | null> | null = null;

/**
 * The request that is out right now, if any, so callers that ask at the same
 * moment share one. At load that is the route guard and the shell, which both
 * want the answer before anything renders.
 */
let inFlight: Promise<CurrentUser | null> | null = null;

/**
 * Which sign-in the answers belong to. Bumped by `forgetSession()`, so a refresh
 * that was already out when someone signed out can tell, when it lands, that its
 * answer is about the person who left — and not put them back.
 */
let sessionEpoch = 0;

export function forgetSession(): void {
  pending = null;
  inFlight = null;
  sessionEpoch += 1;
}

async function fetchCurrentUser(): Promise<CurrentUser | null> {
  try {
    const response = await apiFetch("/api/me");
    if (!response.ok) return null;
    return (await response.json()) as CurrentUser;
  } catch (error) {
    /* 401 is the expected "nobody is signed in", not a failure to report. */
    if (error instanceof UnauthorizedError) return null;
    throw error;
  }
}

function fetchShared(): Promise<CurrentUser | null> {
  if (inFlight) return inFlight;
  const attempt: Promise<CurrentUser | null> = fetchCurrentUser().finally(
    () => {
      // Only if it is still the current one: a sign-out in the meantime has
      // already moved on, and the next request is not this one's to clear.
      if (inFlight === attempt) inFlight = null;
    },
  );
  inFlight = attempt;
  return attempt;
}

/** The signed-in user, or null. Cached; `forgetSession()` invalidates it. */
export function loadCurrentUser(): Promise<CurrentUser | null> {
  if (!LIVE) return Promise.resolve(null);
  pending ??= fetchShared();
  return pending;
}

/**
 * The signed-in user as the server sees them *now* — a request to `/api/me`
 * every time, which is what `useCurrentUser` makes of it: React Query decides
 * when (a tab regaining focus, a screen mounting once the last answer is
 * thirty seconds old), and this makes sure the answer is new when it does.
 *
 * What the guard holds is replaced by a newer user, so the two cannot disagree
 * about who is signed in. It is never replaced by a failure: a refetch that
 * gets a 5xx or no network is a blip, not a sign-out, and sending someone to
 * the sign-in screen over one would be worse than showing them permissions
 * thirty seconds old. A session that has really ended is found the way it
 * always was, on the next reload (see `shellRoute`'s guard).
 */
export async function refreshCurrentUser(): Promise<CurrentUser | null> {
  if (!LIVE) return null;
  const asked = sessionEpoch;
  const user = await fetchShared();
  // Only for the sign-in it was asked under: after a sign-out the guard holds
  // whatever the next person's request has put there, not this.
  if (user && asked === sessionEpoch) pending = Promise.resolve(user);
  return user;
}

/**
 * Whether this browser has a session — what the route guard asks.
 *
 * Against the API it is the server's answer, so a deleted or expired session
 * sends the user back to the sign-in screen on their next navigation rather
 * than leaving them in a shell whose every request fails.
 */
export async function hasSession(): Promise<boolean> {
  if (!LIVE) return hasStoredSession();
  return (await loadCurrentUser()) !== null;
}

/** Signs in, or throws `SignInError` with something worth showing. */
export async function signIn(
  email: string,
  password: string,
): Promise<CurrentUser | null> {
  if (!LIVE) {
    writeStoredSession({ activeRoleId: "role_admin" });
    return null;
  }

  let response: Response;
  try {
    response = await apiFetch("/api/auth/sign-in/email", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ email, password }),
    });
  } catch (error) {
    /*
      Better-Auth answers a wrong password with 401, which `apiFetch` raises
      as `UnauthorizedError` — here that is the ordinary case, not an expired
      session. Anything else means the API could not be reached at all.
    */
    if (error instanceof UnauthorizedError) {
      throw new SignInError("Incorrect email or password.");
    }
    throw new SignInError("Cannot reach the server. Try again in a moment.");
  }

  if (!response.ok) {
    /*
      One message for a wrong password and for an unknown address, which is
      deliberate: telling a stranger which addresses exist is how an account
      list leaks.
    */
    throw new SignInError("Incorrect email or password.");
  }

  forgetSession();
  return loadCurrentUser();
}

/**
 * "Forgot password" (M10-6).
 *
 * Never throws and never reports whether the address exists — the same
 * silence the API's own `/api/auth/request-password-reset` answers with, so
 * this form cannot be used to discover which addresses have an account.
 * Swallowing a network failure the same way is deliberate for the same
 * reason: a stranger probing this form learns nothing from a difference
 * between "no such address" and "the server is unreachable."
 */
export async function requestPasswordReset(email: string): Promise<void> {
  if (!LIVE) return;
  try {
    await apiFetch("/api/auth/request-password-reset", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ email }),
    });
  } catch {
    // See the doc comment above — the caller shows the same message either way.
  }
}

/**
 * Finishes an invite or a password reset — the one mechanism both share
 * (M10-6, M10-7). `token` is whatever `/set-password?token=…` was opened
 * with; the API tells `invited` from a plain reset apart itself, by
 * whether the account already had a password.
 */
export async function setPassword(
  token: string,
  newPassword: string,
): Promise<{ ok: true } | { ok: false; message: string }> {
  if (!LIVE) {
    return {
      ok: false,
      message: "This demo has no backend to set a password against.",
    };
  }

  let response: Response;
  try {
    response = await apiFetch("/api/auth/reset-password", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ newPassword, token }),
    });
  } catch {
    return {
      ok: false,
      message: "Cannot reach the server. Try again in a moment.",
    };
  }

  if (response.ok) return { ok: true };

  const body = (await response.json().catch(() => null)) as {
    message?: string;
  } | null;
  return {
    ok: false,
    message: body?.message ?? "This link is invalid or has expired.",
  };
}

/** Signs out. The session row is deleted, so the cookie is worth nothing. */
export async function signOut(): Promise<void> {
  if (LIVE) {
    try {
      await apiFetch("/api/auth/sign-out", { method: "POST" });
    } catch {
      /*
        Sign out locally regardless. A failed call leaves a row on the server
        that expires on its own; refusing to sign out because the network
        blinked would be worse.
      */
    }
  }
  // The demo session record: only fixtures ever write it, so a build with an
  // API has nothing to clear and must not carry its storage key (#429).
  if (!LIVE) clearStoredSession();
  forgetSession();
}
